import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

// active = current step; filled = has data; empty = no data yet. Drives the box + title/icon colour.
export type BoxState = "active" | "filled" | "empty";
export const boxState = (active: boolean, hasData: boolean): BoxState => (active ? "active" : hasData ? "filled" : "empty");
const toneClass = (state: BoxState) =>
  state === "active" ? "text-primary" : state === "filled" ? "text-neutral-dark-gray" : "text-neutral-silver-gray";

/**
 * A box in the Booking Summary: header (icon + title, optional action) + optional data body.
 * The single card shell for BOTH summary variants (draft steps and the priced Payment step),
 * so the panel reads as one system across the wizard.
 *
 * This DISPLAYS a step — its tone tracks whether that step is active, has data, or is still empty.
 * It is deliberately not the shell for a group of form inputs, which looks similar and means
 * something else; the bulk edit modal borrowed it once and had to widen it to do so. See that
 * modal's own `Section`.
 */
export const SummaryBox = ({
  icon,
  title,
  state,
  action,
  className,
  children,
}: React.PropsWithChildren<{ icon: string; title: string; state: BoxState; action?: React.ReactNode; className?: string }>) => (
  <div
    className={clsx(
      "rounded-lg border p-3",
      state === "active" ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray",
      className
    )}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Icon name={icon} size="lg" className={toneClass(state)} />
        <Typography variant="action" className={toneClass(state)}>
          {title}
        </Typography>
      </div>
      {action}
    </div>
    {children && <div className="mt-3">{children}</div>}
  </div>
);

export const Stacked = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className="min-w-0">
    <Typography variant="action" className="block truncate">
      {title}
    </Typography>
    <Typography variant="paragraph" className="block text-neutral-dark-gray">
      {subtitle}
    </Typography>
  </div>
);
